import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, XCircleIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, RefreshCwIcon, EyeIcon, CalendarIcon, ArrowUpDownIcon, BuildingIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
// Mock merchants data
const merchantsData = [{
  id: 'M001',
  name: 'Global Electronics',
  businessType: 'Electronics',
  status: 'active',
  contactPerson: 'Michael Lee',
  email: 'michael@globalelectronics.com',
  phone: '+1 (555) 123-4567',
  address: '123 Main St, Downtown, NY 10001',
  registrationDate: '2022-03-15T10:20:00Z',
  totalTransactions: 1256,
  successRate: 98.2
}, {
  id: 'M002',
  name: 'Fashion Forward',
  businessType: 'Apparel',
  status: 'active',
  contactPerson: 'Sophia Martinez',
  email: 'sophia@fashionforward.com',
  phone: '+1 (555) 987-6543',
  address: '456 Fashion Ave, Midtown, NY 10018',
  registrationDate: '2022-05-22T14:15:00Z',
  totalTransactions: 875,
  successRate: 97.5
}, {
  id: 'M003',
  name: 'Gourmet Coffee Shop',
  businessType: 'Food & Beverage',
  status: 'inactive',
  contactPerson: 'James Wilson',
  email: 'james@gourmetcoffee.com',
  phone: '+1 (555) 456-7890',
  address: '789 Café Lane, SoHo, NY 10013',
  registrationDate: '2022-01-10T08:20:00Z',
  totalTransactions: 1542,
  successRate: 96.8
}, {
  id: 'M004',
  name: 'Premium Phones',
  businessType: 'Electronics',
  status: 'active',
  contactPerson: 'Emily Brown',
  email: 'emily@premiumphones.com',
  phone: '+1 (555) 234-5678',
  address: '101 Tech Plaza, Financial District, NY 10005',
  registrationDate: '2022-08-05T10:15:00Z',
  totalTransactions: 652,
  successRate: 99.1
}, {
  id: 'M005',
  name: 'Seaside Resort',
  businessType: 'Hospitality',
  status: 'active',
  contactPerson: 'Daniel Smith',
  email: 'daniel@seasideresort.com',
  phone: '+1 (555) 876-5432',
  address: '202 Oceanview Drive, Hamptons, NY 11937',
  registrationDate: '2022-06-15T12:20:00Z',
  totalTransactions: 987,
  successRate: 98.7
}];
// Mock transactions data
const generateTransactions = (merchantId: string) => {
  const statuses = ['completed', 'pending', 'rejected', 'refunded'];
  const methods = ['QR Code', 'Card Payment', 'Bank Transfer', 'Mobile Wallet'];
  const transactions = [];
  // Generate between 15-25 transactions
  const count = Math.floor(Math.random() * 10) + 15;
  for (let i = 0; i < count; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    // Make sure we have some rejected transactions
    const finalStatus = i < 3 ? 'rejected' : status;
    // Generate date within last 30 days
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));
    date.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60));
    transactions.push({
      id: `TX${merchantId.substring(1)}${i.toString().padStart(3, '0')}`,
      merchantId,
      amount: Math.floor(Math.random() * 1000000) / 100,
      status: finalStatus,
      method: methods[Math.floor(Math.random() * methods.length)],
      date: date.toISOString(),
      customer: `Customer ${i + 1}`,
      reference: `REF-${merchantId}-${i}`,
      description: `Transaction for ${merchantId}`,
      rejectionReason: finalStatus === 'rejected' ? ['Insufficient funds', 'Suspicious activity', 'Payment limit exceeded', 'Card expired'][Math.floor(Math.random() * 4)] : null
    });
  }
  // Sort by date descending
  return transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};
// Transaction type
interface Transaction {
  id: string;
  merchantId: string;
  amount: number;
  status: string;
  method: string;
  date: string;
  customer: string;
  reference: string;
  description: string;
  rejectionReason: string | null;
}
// Merchant type
interface Merchant {
  id: string;
  name: string;
  businessType: string;
  status: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  registrationDate: string;
  totalTransactions: number;
  successRate: number;
}
const MerchantPaymentMonitor: React.FC = () => {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>(merchantsData);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showMerchantDetails, setShowMerchantDetails] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [onlyRejected, setOnlyRejected] = useState(false);
  // Sorting
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // When a merchant is selected, load their transactions
  useEffect(() => {
    if (selectedMerchant) {
      const merchantTransactions = generateTransactions(selectedMerchant.id);
      setTransactions(merchantTransactions);
      setFilteredTransactions(merchantTransactions);
    } else {
      setTransactions([]);
      setFilteredTransactions([]);
    }
  }, [selectedMerchant]);
  // Filter merchants based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = merchants.filter(merchant => merchant.name.toLowerCase().includes(searchTerm.toLowerCase()) || merchant.id.toLowerCase().includes(searchTerm.toLowerCase()) || merchant.businessType.toLowerCase().includes(searchTerm.toLowerCase()) || merchant.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredMerchants(filtered);
    } else {
      setFilteredMerchants(merchants);
    }
  }, [searchTerm, merchants]);
  // Apply transaction filters
  const applyTransactionFilters = () => {
    let filtered = [...transactions];
    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    // Only rejected filter (overrides status filter)
    if (onlyRejected) {
      filtered = filtered.filter(t => t.status === 'rejected');
    }
    // Method filter
    if (methodFilter) {
      filtered = filtered.filter(t => t.method === methodFilter);
    }
    // Date range filter
    if (dateFromFilter) {
      const fromDate = new Date(dateFromFilter);
      filtered = filtered.filter(t => new Date(t.date) >= fromDate);
    }
    if (dateToFilter) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(t => new Date(t.date) <= toDate);
    }
    // Apply sorting
    filtered = filtered.sort((a, b) => {
      const fieldA = a[sortField as keyof Transaction];
      const fieldB = b[sortField as keyof Transaction];
      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
      } else {
        // Assuming numeric comparison
        return sortDirection === 'asc' ? (fieldA as number) - (fieldB as number) : (fieldB as number) - (fieldA as number);
      }
    });
    setFilteredTransactions(filtered);
  };
  // Apply filters when they change
  useEffect(() => {
    applyTransactionFilters();
  }, [statusFilter, dateFromFilter, dateToFilter, methodFilter, onlyRejected, sortField, sortDirection, transactions]);
  // Reset filters
  const resetFilters = () => {
    setStatusFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setMethodFilter('');
    setOnlyRejected(false);
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  // Format date and time
  const formatDateTime = (dateString: string) => {
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };
  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3274">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3275" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" data-id="element-3276">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-3277" />
            Pending
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-3278">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-3279" />
            Rejected
          </span>;
      case 'refunded':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-3280">
            <RefreshCwIcon className="w-3 h-3 mr-1" data-id="element-3281" />
            Refunded
          </span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3282">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3283" />
            Active
          </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-3284">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-3285" />
            Inactive
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-3286">
            {status}
          </span>;
    }
  };
  // View merchant details
  const viewMerchantDetails = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowMerchantDetails(true);
  };
  // View transaction details
  const viewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };
  // Select merchant to view transactions
  const selectMerchant = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    resetFilters();
  };
  // Clear selected merchant
  const clearSelectedMerchant = () => {
    setSelectedMerchant(null);
    setTransactions([]);
    setFilteredTransactions([]);
  };
  return <div className="space-y-6" data-id="element-3287">
      <div data-id="element-3288">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3289">
          Merchant Payment Monitor
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3290">
          Search merchants, view their payment details and transaction history
        </p>
      </div>
      {/* Merchant Search & Selection */}
      {!selectedMerchant ? <>
          <div className="relative" data-id="element-3291">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3292">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3293" />
            </div>
            <input type="text" placeholder="Search merchants by name, ID, or business type..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3294" />
          </div>
          <Card data-id="element-3295">
            <div className="overflow-x-auto" data-id="element-3296">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3297">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-3298">
                  <tr data-id="element-3299">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3300">
                      Merchant
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3301">
                      Business Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3302">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3303">
                      Total Transactions
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3304">
                      Success Rate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3305">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3306">
                  {filteredMerchants.map(merchant => <tr key={merchant.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-3307">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3308">
                        <div className="flex items-center" data-id="element-3309">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-3310">
                            <BuildingIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-3311" />
                          </div>
                          <div className="ml-4" data-id="element-3312">
                            <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3313">
                              {merchant.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3314">
                              {merchant.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3315">
                        {merchant.businessType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3316">
                        {getStatusBadge(merchant.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3317">
                        {merchant.totalTransactions.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3318">
                        <div className="flex items-center" data-id="element-3319">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2 max-w-[100px]" data-id="element-3320">
                            <div className={`h-2.5 rounded-full ${merchant.successRate >= 98 ? 'bg-green-500' : merchant.successRate >= 95 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                        width: `${merchant.successRate}%`
                      }} data-id="element-3321"></div>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3322">
                            {merchant.successRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3323">
                        <div className="flex space-x-2" data-id="element-3324">
                          <button onClick={() => viewMerchantDetails(merchant)} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" title="View Details" data-id="element-3325">
                            <InfoIcon className="h-5 w-5" data-id="element-3326" />
                          </button>
                          <button onClick={() => selectMerchant(merchant)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="View Transactions" data-id="element-3327">
                            <EyeIcon className="h-5 w-5" data-id="element-3328" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                  {filteredMerchants.length === 0 && <tr data-id="element-3329">
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-3330">
                        No merchants found matching your search.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </> : <>
          {/* Selected Merchant & Transactions View */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-3331">
            <div data-id="element-3332">
              <button onClick={clearSelectedMerchant} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2" data-id="element-3333">
                <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3334" />
                Back to Merchant List
              </button>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center" data-id="element-3335">
                {selectedMerchant.name}
                <span className="ml-2" data-id="element-3336">
                  {getStatusBadge(selectedMerchant.status)}
                </span>
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3337">
                {selectedMerchant.id} • {selectedMerchant.businessType}
              </p>
            </div>
            <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-3338">
              <button onClick={() => viewMerchantDetails(selectedMerchant)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3339">
                <InfoIcon className="h-4 w-4 mr-2" data-id="element-3340" />
                Merchant Details
              </button>
              <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3341">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-3342" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3343">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3344" />
                Export
              </button>
            </div>
          </div>
          {/* Transaction Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3345">
            <Card className="bg-white dark:bg-gray-800" data-id="element-3346">
              <div className="flex items-center" data-id="element-3347">
                <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30 mr-4" data-id="element-3348">
                  <ArrowUpDownIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-3349" />
                </div>
                <div data-id="element-3350">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3351">
                    Total Transactions
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3352">
                    {transactions.length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white dark:bg-gray-800" data-id="element-3353">
              <div className="flex items-center" data-id="element-3354">
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30 mr-4" data-id="element-3355">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-3356" />
                </div>
                <div data-id="element-3357">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3358">
                    Completed
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3359">
                    {transactions.filter(t => t.status === 'completed').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white dark:bg-gray-800" data-id="element-3360">
              <div className="flex items-center" data-id="element-3361">
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30 mr-4" data-id="element-3362">
                  <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-3363" />
                </div>
                <div data-id="element-3364">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3365">
                    Rejected
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3366">
                    {transactions.filter(t => t.status === 'rejected').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white dark:bg-gray-800" data-id="element-3367">
              <div className="flex items-center" data-id="element-3368">
                <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30 mr-4" data-id="element-3369">
                  <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" data-id="element-3370" />
                </div>
                <div data-id="element-3371">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3372">
                    Pending
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3373">
                    {transactions.filter(t => t.status === 'pending').length}
                  </p>
                </div>
              </div>
            </Card>
          </div>
          {/* Transaction Filters */}
          {showFilters && <Card data-id="element-3374">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3375">
                <div data-id="element-3376">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3377">
                    Status
                  </label>
                  <select value={statusFilter} onChange={e => {
              setStatusFilter(e.target.value);
              if (e.target.value !== 'rejected') {
                setOnlyRejected(false);
              }
            }} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" disabled={onlyRejected} data-id="element-3378">
                    <option value="" data-id="element-3379">All Statuses</option>
                    <option value="completed" data-id="element-3380">Completed</option>
                    <option value="pending" data-id="element-3381">Pending</option>
                    <option value="rejected" data-id="element-3382">Rejected</option>
                    <option value="refunded" data-id="element-3383">Refunded</option>
                  </select>
                </div>
                <div data-id="element-3384">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3385">
                    Payment Method
                  </label>
                  <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-3386">
                    <option value="" data-id="element-3387">All Methods</option>
                    <option value="QR Code" data-id="element-3388">QR Code</option>
                    <option value="Card Payment" data-id="element-3389">Card Payment</option>
                    <option value="Bank Transfer" data-id="element-3390">Bank Transfer</option>
                    <option value="Mobile Wallet" data-id="element-3391">Mobile Wallet</option>
                  </select>
                </div>
                <div data-id="element-3392">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3393">
                    Date From
                  </label>
                  <div className="relative" data-id="element-3394">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3395">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-3396" />
                    </div>
                    <input type="date" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800" data-id="element-3397" />
                  </div>
                </div>
                <div data-id="element-3398">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3399">
                    Date To
                  </label>
                  <div className="relative" data-id="element-3400">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3401">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-3402" />
                    </div>
                    <input type="date" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800" data-id="element-3403" />
                  </div>
                </div>
                <div className="flex items-center" data-id="element-3404">
                  <input id="only-rejected" type="checkbox" checked={onlyRejected} onChange={e => {
              setOnlyRejected(e.target.checked);
              if (e.target.checked) {
                setStatusFilter('rejected');
              }
            }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-3405" />
                  <label htmlFor="only-rejected" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-3406">
                    Show only rejected transactions
                  </label>
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-3" data-id="element-3407">
                <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3408">
                  Reset
                </button>
                <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700" data-id="element-3409">
                  Apply Filters
                </button>
              </div>
            </Card>}
          {/* Transactions Table */}
          <Card data-id="element-3410">
            <div className="mb-4 flex items-center justify-between" data-id="element-3411">
              <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3412">
                Showing {filteredTransactions.length} of {transactions.length}{' '}
                transactions
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-3413">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3414">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-3415">
                  <tr data-id="element-3416">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-3417">
                      <div className="flex items-center" data-id="element-3418">
                        Transaction ID
                        {sortField === 'id' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3419" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3420" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('date')} data-id="element-3421">
                      <div className="flex items-center" data-id="element-3422">
                        Date & Time
                        {sortField === 'date' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3423" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3424" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-3425">
                      <div className="flex items-center" data-id="element-3426">
                        Amount
                        {sortField === 'amount' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3427" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3428" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('method')} data-id="element-3429">
                      <div className="flex items-center" data-id="element-3430">
                        Method
                        {sortField === 'method' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3431" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3432" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-3433">
                      <div className="flex items-center" data-id="element-3434">
                        Status
                        {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3435" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3436" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3437">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3438">
                  {filteredTransactions.map(transaction => <tr key={transaction.id} className={`hover:bg-gray-50 dark:hover:bg-gray-750 ${transaction.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/10' : ''}`} data-id="element-3439">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-3440">
                        {transaction.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3441">
                        <div data-id="element-3442">{formatDate(transaction.date)}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500" data-id="element-3443">
                          {formatTime(transaction.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3444">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3445">
                        {transaction.method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3446">
                        {getStatusBadge(transaction.status)}
                        {transaction.status === 'rejected' && transaction.rejectionReason && <div className="text-xs text-red-600 dark:text-red-400 mt-1" data-id="element-3447">
                              {transaction.rejectionReason}
                            </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3448">
                        <button onClick={() => viewTransactionDetails(transaction)} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" data-id="element-3449">
                          <EyeIcon className="h-5 w-5" data-id="element-3450" />
                        </button>
                      </td>
                    </tr>)}
                  {filteredTransactions.length === 0 && <tr data-id="element-3451">
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-3452">
                        No transactions found matching your filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>}
      {/* Merchant Details Modal */}
      <Modal isOpen={showMerchantDetails} onClose={() => setShowMerchantDetails(false)} title="Merchant Details" size="lg" data-id="element-3453">
        {selectedMerchant && <div className="space-y-6" data-id="element-3454">
            <div className="flex items-center" data-id="element-3455">
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30 mr-4" data-id="element-3456">
                <BuildingIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-3457" />
              </div>
              <div data-id="element-3458">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3459">
                  {selectedMerchant.name}
                </h3>
                <div className="flex items-center mt-1" data-id="element-3460">
                  {getStatusBadge(selectedMerchant.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-3461">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3462">
                    {selectedMerchant.businessType}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3463">
              <div data-id="element-3464">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-3465">
                  Contact Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-3466">
                  <div className="flex items-start" data-id="element-3467">
                    <UserIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-3468" />
                    <div data-id="element-3469">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3470">
                        Contact Person
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3471">
                        {selectedMerchant.contactPerson}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3472">
                    <MailIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-3473" />
                    <div data-id="element-3474">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3475">
                        Email
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3476">
                        {selectedMerchant.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3477">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-3478" />
                    <div data-id="element-3479">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3480">
                        Phone
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3481">
                        {selectedMerchant.phone}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3482">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-3483" />
                    <div data-id="element-3484">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3485">
                        Address
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3486">
                        {selectedMerchant.address}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-3487">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-3488">
                  Merchant Statistics
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-3489">
                  <div className="flex items-center justify-between" data-id="element-3490">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3491">
                      Registration Date:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3492">
                      {formatDate(selectedMerchant.registrationDate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3493">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3494">
                      Total Transactions:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3495">
                      {selectedMerchant.totalTransactions.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3496">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3497">
                      Success Rate:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3498">
                      {selectedMerchant.successRate}%
                    </span>
                  </div>
                  <div data-id="element-3499">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3500">
                      Transaction Success:
                    </span>
                    <div className="mt-2" data-id="element-3501">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-3502">
                        <div className={`h-2.5 rounded-full ${selectedMerchant.successRate >= 98 ? 'bg-green-500' : selectedMerchant.successRate >= 95 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${selectedMerchant.successRate}%`
                    }} data-id="element-3503"></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3504">
                        <span data-id="element-3505">0%</span>
                        <span data-id="element-3506">50%</span>
                        <span data-id="element-3507">100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-3508">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-3509">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-3510">
                <button onClick={() => {
              setShowMerchantDetails(false);
              if (!selectedMerchant) {
                selectMerchant(selectedMerchant);
              }
            }} className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-3511">
                  View Transactions
                </button>
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-3512">
                  Download Reports
                </button>
                <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-3513">
                  Contact Merchant
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Transaction Details Modal */}
      <Modal isOpen={showTransactionDetails} onClose={() => setShowTransactionDetails(false)} title="Transaction Details" size="lg" data-id="element-3514">
        {selectedTransaction && <div className="space-y-6" data-id="element-3515">
            <div className="flex items-center justify-between" data-id="element-3516">
              <div data-id="element-3517">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3518">
                  {selectedTransaction.id}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3519">
                  {formatDateTime(selectedTransaction.date)}
                </p>
              </div>
              <div data-id="element-3520">{getStatusBadge(selectedTransaction.status)}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3521">
              <div data-id="element-3522">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-3523">
                  Transaction Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-3524">
                  <div className="flex justify-between" data-id="element-3525">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3526">
                      Amount:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3527">
                      {formatCurrency(selectedTransaction.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3528">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3529">
                      Payment Method:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3530">
                      {selectedTransaction.method}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3531">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3532">
                      Reference:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3533">
                      {selectedTransaction.reference}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3534">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3535">
                      Description:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3536">
                      {selectedTransaction.description}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-3537">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-3538">
                  Additional Details
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-3539">
                  <div className="flex justify-between" data-id="element-3540">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3541">
                      Customer:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3542">
                      {selectedTransaction.customer}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3543">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3544">
                      Merchant ID:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3545">
                      {selectedTransaction.merchantId}
                    </span>
                  </div>
                  {selectedTransaction.status === 'rejected' && selectedTransaction.rejectionReason && <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-3546">
                        <div className="text-sm font-medium text-red-600 dark:text-red-400" data-id="element-3547">
                          Rejection Reason:
                        </div>
                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-100 dark:border-red-800" data-id="element-3548">
                          {selectedTransaction.rejectionReason}
                        </div>
                      </div>}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-3549">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-3550">
                Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-3551">
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-3552">
                  Download Receipt
                </button>
                {selectedTransaction.status === 'rejected' && <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-3553">
                    Retry Transaction
                  </button>}
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-3554">
                  View Full Details
                </button>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default MerchantPaymentMonitor;